import React, { useRef, useEffect } from 'react';
import { hasForegroundLayer } from './proceduralBackgrounds';
import { registerForegroundCanvas, unregisterForegroundCanvas } from './ProceduralBackground';

/**
 * ProceduralForeground — renders the atmospheric wisp layer ON TOP of game content.
 *
 * This is the companion to ProceduralBackground. While the background renders
 * behind the game canvas at zIndex 0, this overlay renders front cloud wisps
 * on top at a high zIndex — creating the parallax depth effect.
 *
 * Only renders for backgrounds that have a foreground layer (skyClouds, sunsetGlow).
 * For others (nightStars, neonGrid, oceanWaves), renders nothing.
 *
 * NO separate RAF loop — this component registers its canvas element with
 * ProceduralBackground's shared store so the background's single RAF loop
 * drives both canvases. This halves the number of animation frames for
 * procedural backgrounds with foreground layers.
 *
 * @param {string}  rendererKey  - e.g. "skyClouds", "sunsetGlow"
 */
export default function ProceduralForeground({ rendererKey }) {
  const canvasRef = useRef(null);

  // Don't render anything if this background type has no foreground layer
  const hasFg = hasForegroundLayer(rendererKey);

  // Register/unregister canvas with ProceduralBackground's RAF loop
  useEffect(() => {
    if (!hasFg || !canvasRef.current) return;
    registerForegroundCanvas(rendererKey, canvasRef.current);
    return () => {
      unregisterForegroundCanvas(rendererKey);
    };
  }, [rendererKey, hasFg]);

  if (!hasFg) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 6,
        pointerEvents: 'none',
      }}
    />
  );
}
